// src/components/TablaTransportadora.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../style/Transportadora.module.css";

const PEDIDOS_URL = "https://marketbusters.pro/Dropdrop/pedidos_listado_novedad_encamino.json";

/* ===== cookies + LS ===== */
const getCookie = (name) => {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  try { return m ? decodeURIComponent(m[2]) : null; } catch { return null; }
};
const setCookie = (name, value, days = 180) => {
  try {
    const encoded = encodeURIComponent(value);
    if (encoded.length > 3800) {
      localStorage.setItem(name, value);
      document.cookie = `${name}=__LS__; path=/; max-age=${days * 86400}; samesite=Lax`;
      return;
    }
    document.cookie = `${name}=${encoded}; path=/; max-age=${days * 86400}; samesite=Lax`;
  } catch {
    localStorage.setItem(name, value);
  }
};
const getCookieOrLSJson = (name, fallback = {}) => {
  const raw = getCookie(name);
  let txt = raw;
  if (raw === "__LS__" || raw == null) {
    const ls = localStorage.getItem(name);
    if (ls) txt = ls;
  }
  try { return txt ? JSON.parse(txt) : fallback; } catch { return fallback; }
};
const setCookieJson = (name, obj) => setCookie(name, JSON.stringify(obj ?? {}));

/* ===== utils ===== */
const cleanUbigeo = (input) => {
  let s = String(input || "");
  s = s.replace(/regi[oó]n\s+metropolitana\s+de\s+santiago,\s*/iu, "");
  s = s.trim();
  const toTitle = (str) => str.toLowerCase().split(/\s+/).map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(" ");
  const plain = s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const fixes = { nunoa: "Ñuñoa", maipu: "Maipú", "san joaquin": "San Joaquín", "estacion central": "Estación Central", conchali: "Conchalí" };
  if (fixes[plain]) return fixes[plain];
  return toTitle(s);
};
const formatCLP = (val) => {
  const n = Number(String(val).replace(/[^0-9.-]/g, ""));
  if (!isFinite(n)) return String(val ?? "");
  return new Intl.NumberFormat("es-CL", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(n));
};
const normTracking = (value) => {
  const v = String(value || "").toLowerCase();
  if (v.includes("novedad")) return "Novedad";
  if (v.includes("camino")) return "En camino";
  return value || "";
};

// Parse seguro para "YYYY-MM-DD HH:mm:ss" a Date local
const parseLocalTS = (s) => {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/);
  if (!m) return null;
  const [_, Y, M, D, h, m2, s2] = m;
  return new Date(Number(Y), Number(M) - 1, Number(D), Number(h), Number(m2), Number(s2));
};
// Diferencia en "dd día(s) hh:mm"
const diffHHMM = (from, to) => {
  if (!from || !to) return null;
  let ms = to.getTime() - from.getTime();
  if (ms < 0) ms = 0;

  const totalMin = Math.floor(ms / 60000);
  const dayMin = 24 * 60;

  const dd = Math.floor(totalMin / dayMin);
  const remMin = totalMin % dayMin;
  const hh = Math.floor(remMin / 60);
  const mm = remMin % 60;

  const ddStr = String(dd).padStart(2, "0");           // "02"
  const label = dd === 1 ? "día" : "días";             // singular/plural
  const hhStr = String(hh).padStart(2, "0");
  const mmStr = String(mm).padStart(2, "0");

  return `${ddStr} ${label} ${hhStr}:${mmStr}`;
};

/* ===== chips/emojis ===== */
const TrackingChip = ({ value }) => {
  const v = normTracking(value);
  const base = {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "9999px",
    fontSize: 12,
    fontWeight: 800,
    color: "#fff",
    border: "1px solid #000",
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  };
  if (v === "Novedad") return <span style={{ ...base, background: "#f59e0b" }}>Novedad</span>;
  if (v === "En camino") return <span style={{ ...base, background: "#2563eb" }}>En camino</span>;
  return <span style={{ ...base, background: "#6b7280" }}>{v || "—"}</span>;
};
const RespuestaEmoji = ({ tracking, respuesta }) => {
  const t = normTracking(tracking);
  if (t !== "Novedad") return null;
  const r = String(respuesta ?? "").trim();
  if (r === "1") return <span title="Respuesta novedad: ✅" style={{ marginLeft: 8, fontSize: 16 }}>✅</span>;
  if (r === "0") return <span title="Respuesta novedad: ❌" style={{ marginLeft: 8, fontSize: 16 }}>❌</span>;
  return null;
};

// Pequeño badge ⏱ HH:MM
const TimeBadge = ({ fromTS, toDate, title }) => {
  const from = parseLocalTS(fromTS);
  const delta = from ? diffHHMM(from, toDate) : null;
  if (!delta) return null;
  return (
    <span
      title={title}
      style={{ marginLeft: 8, fontSize: 16, color: "#9ca3af", fontWeight: 700, whiteSpace: "nowrap" }}
    >
      ⏱ {delta}
    </span>
  );
};

/* ===== columnas ===== */
const COLUMNS = [
  { key: "nombreNegocioTienda", label: "Tienda" },
  { key: "id", label: "ID" },
  { key: "fechaEmision", label: "Fecha Emisión" },
  { key: "nombreComprador", label: "Comprador" },
  { key: "celularComprador", label: "Celular" },
  { key: "ubigeoComprador", label: "Ubigeo" },
  { key: "direccionComprador", label: "Dirección" },
  { key: "total", label: "Total" },
  { key: "nombreNegocioProveedor", label: "Proveedor" },
  { key: "productoProveedor", label: "Producto (Proveedor)" },
  { key: "totalProveedor", label: "Total Proveedor" },
  { key: "trackindPedido", label: "Tracking" },
  { key: "contacto", label: "Contacto" },
  { key: "idGuia", label: "Guía" },
];

export default function TablaTransportadora({ refreshKey }) {
  const [pedidos, setPedidos] = useState([]);
  const [loadingPedidos, setLoadingPedidos] = useState(false);
  const [errorPedidos, setErrorPedidos] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null); // ← última actualización

  const [colWidths, setColWidths] = useState(() => getCookieOrLSJson("mb_col_widths", {}));
  const [filters, setFilters] = useState(() => getCookieOrLSJson("mb_table_filters", {}));
  const [contactMap, setContactMap] = useState(() => getCookieOrLSJson("mb_contact_status", {}));

  // visibilidad de columnas
  const defaultVisibility = () => { const o = {}; for (const c of COLUMNS) o[c.key] = true; return o; };
  const [colVisibility, setColVisibility] = useState(() => ({ ...defaultVisibility(), ...getCookieOrLSJson("mb_col_visibility", {}) }));
  const [showColPanel, setShowColPanel] = useState(false);

  useEffect(() => setCookieJson("mb_col_widths", colWidths), [colWidths]);
  useEffect(() => setCookieJson("mb_table_filters", filters), [filters]);
  useEffect(() => setCookieJson("mb_contact_status", contactMap), [contactMap]);
  useEffect(() => setCookieJson("mb_col_visibility", colVisibility), [colVisibility]);

  // cargar pedidos
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        setLoadingPedidos(true); setErrorPedidos("");
        const res = await fetch(PEDIDOS_URL, { cache: "no-store", mode: "cors" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const items = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        const cleaned = items.map((it) => {
          const row = {};
          COLUMNS.forEach(({ key }) => { if (key !== "contacto") row[key] = it?.[key] ?? ""; });
          // extras para tiempo
          row.respuestaNovedadTienda = it?.respuestaNovedadTienda ?? "";
          row.fechaNovedad = it?.fechaNovedad ?? "";
          row.fechaRecolectado = it?.fechaRecolectado ?? "";
          return row;
        });
        if (!abort) setPedidos(cleaned);
      } catch (e) {
        if (!abort) setErrorPedidos(`No se pudo cargar pedidos: ${e.message}`);
      } finally {
        if (!abort) setLastUpdatedAt(new Date()); // ← marcamos la hora de la última actualización
        if (!abort) setLoadingPedidos(false);
      }
    })();
    return () => void (abort = true);
  }, [refreshKey]);

  /* ===== resize columnas ===== */
  const thRefs = useRef({});
  const resizingRef = useRef(null);
  const onMouseMove = (e) => {
    const r = resizingRef.current; if (!r) return;
    const delta = e.clientX - r.startX; const newW = Math.max(90, r.startW + delta);
    setColWidths((prev) => ({ ...prev, [r.key]: newW }));
  };
  const onMouseUp = () => {
    resizingRef.current = null;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
  };
  const startResize = (key, e) => {
    const el = thRefs.current[key];
    const startW = el ? el.offsetWidth : (colWidths[key] || 120);
    resizingRef.current = { key, startX: e.clientX, startW };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    e.preventDefault(); e.stopPropagation();
  };

  /* ===== display helpers ===== */
  const cellDisplay = (key, row) => {
    if (key === "ubigeoComprador") return cleanUbigeo(row[key]);
    if (key === "total" || key === "totalProveedor") return formatCLP(row[key]);
    if (key === "trackindPedido") return normTracking(row[key]);
    if (key === "contacto") {
      const current = contactMap[row.id] ?? "no";
      return current === "contactado" ? "Contactado" : "No contactado";
    }
    return row[key] ?? "";
  };

  /* ===== opciones por columna ===== */
  const optionsByColumn = useMemo(() => {
    const map = {};
    for (const col of COLUMNS) {
      if (col.key === "contacto") { map[col.key] = ["Todos", "Contactado", "No contactado"]; continue; }
      if (col.key === "trackindPedido") {
        const s = new Set();
        for (const r of pedidos) { const v = cellDisplay(col.key, r); if (v) s.add(v); }
        s.add("Novedad"); s.add("En camino");
        map[col.key] = ["Todos", ...Array.from(s).sort((a, b) => a.localeCompare(b, "es"))];
        continue;
      }
      const set = new Set();
      for (const r of pedidos) { const v = cellDisplay(col.key, r); if (v !== "") set.add(String(v)); }
      map[col.key] = ["Todos", ...Array.from(set).sort((a, b) => a.localeCompare(b, "es"))];
    }
    return map;
  }, [pedidos, contactMap]);

  /* ===== filtros ===== */
  const filteredPedidos = useMemo(() => {
    const active = Object.fromEntries(Object.entries(filters || {}).filter(([k, v]) => v && v !== "Todos"));
    if (Object.keys(active).length === 0) return pedidos;

    return pedidos.filter((row) => {
      // filtro botón respuesta novedad
      const respSel = active["respuestaNovedad"]; // "Ticket" | "X"
      if (respSel) {
        const t = normTracking(row.trackindPedido);
        if (t === "En camino") {
          // se mantienen
        } else if (t === "Novedad") {
          const r = String(row.respuestaNovedadTienda ?? "").trim();
          const ok = (respSel === "Ticket" && r === "1") || (respSel === "X" && r === "0");
          if (!ok) return false;
        } else {
          return false;
        }
      }

      // resto de filtros por columna
      for (const [key, sel] of Object.entries(active)) {
        if (key === "respuestaNovedad") continue;
        if (key === "contacto") {
          const current = contactMap[row.id] ?? "no";
          const asText = current === "contactado" ? "Contactado" : "No contactado";
          if (asText !== sel) return false;
        } else {
          if (cellDisplay(key, row) !== sel) return false;
        }
      }
      return true;
    });
  }, [pedidos, filters, contactMap]);

  // contadores (en base a filtrados actuales)
  const { novTickerCount, enCaminoCount } = useMemo(() => {
    let nov = 0, enc = 0;
    for (const row of filteredPedidos) {
      const t = normTracking(row.trackindPedido);
      if (t === "En camino") enc++;
      else if (t === "Novedad" && String(row.respuestaNovedadTienda ?? "").trim() === "1") nov++;
    }
    return { novTickerCount: nov, enCaminoCount: enc };
  }, [filteredPedidos]);

  /* ===== header cell ===== */
  const HeaderCell = ({ col }) => {
    if (colVisibility[col.key] === false) return null;
    const widthPx = colWidths[col.key];
    return (
      <th
        ref={(el) => (thRefs.current[col.key] = el)}
        className={styles.th}
        style={{
          position: "sticky", top: 0, zIndex: 2,
          width: widthPx ? `${widthPx}px` : undefined,
          minWidth: widthPx ? `${widthPx}px` : undefined,
          maxWidth: widthPx ? `${widthPx}px` : undefined,
          paddingRight: 18,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
          <span>{col.label}</span>
          <span
            onMouseDown={(e) => startResize(col.key, e)}
            title="Arrastra para ajustar ancho"
            style={{ cursor: "col-resize", userSelect: "none", width: 8, height: 18, borderLeft: "2px solid rgba(255,255,255,0.2)", display: "inline-block" }}
          />
        </div>
        <div style={{ marginTop: 6 }}>
          <select
            value={filters[col.key] ?? "Todos"}
            onChange={(e) => setFilters((f) => ({ ...f, [col.key]: e.target.value }))}
            style={{ width: "100%", background: "#0b0e14", color: "#e5e7eb", border: "1px solid #222633", borderRadius: 8, padding: "4px 6px", fontSize: 12 }}
          >
            {(optionsByColumn[col.key] || ["Todos"]).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
      </th>
    );
  };

  const visibleColumns = COLUMNS.filter((c) => colVisibility[c.key] !== false);

  // botón-filtro (grupo) a la derecha
  const RespBtn = ({ value, label }) => {
    const active = (filters.respuestaNovedad ?? "Todos") === value;
    return (
      <button
        onClick={() => setFilters((f) => ({ ...f, respuestaNovedad: value }))}
        style={{
          padding: "8px 12px",
          borderRadius: 10,
          border: active ? "1px solid #3b82f6" : "1px solid #222633",
          background: active ? "#0b1220" : "#0b0e14",
          color: active ? "#bfdbfe" : "#e5e7eb",
          fontSize: 14,
          fontWeight: 700,
          cursor: "pointer",
          lineHeight: 1,
        }}
        title={`Filtrar por ${label}`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className={styles.card}>
      {/* ======= HEADER UNA FILA ======= */}
      <div
        className={styles.cardHeader}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "nowrap",
        }}
      >
        {/* Izquierda: título + contadores */}
        <div style={{ display: "flex", alignItems: "center", gap: 18, minWidth: 0 }}>
          <h3 className={styles.cardTitle} style={{ margin: 0, whiteSpace: "nowrap" }}>
            Pedidos (Novedad / En camino)
          </h3>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              fontSize: 15,
              color: "#cbd5e1",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 18 }}>✅</span>
              <span>Novedad con ticket:</span>
              <strong style={{ fontSize: 18, color: "#e5e7eb" }}>{novTickerCount}</strong>
            </span>
            <span style={{ opacity: 0.5 }}>•</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 18 }}>🚚</span>
              <span>En camino:</span>
              <strong style={{ fontSize: 18, color: "#e5e7eb" }}>{enCaminoCount}</strong>
            </span>
          </div>
        </div>

        {/* Derecha: [TODOS][✅][❌] + COLUMNAS */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <div role="group" aria-label="Filtro respuesta novedad" style={{ display: "flex", gap: 6 }}>
            <RespBtn value="Todos" label="TODOS" />
            <RespBtn value="Ticket" label="✅" />
            <RespBtn value="X" label="❌" />
          </div>

          <button
            onClick={() => setShowColPanel((v) => !v)}
            style={{
              padding: "8px 12px",
              borderRadius: 10,
              border: "1px solid #222633",
              background: "#0b0e14",
              color: "#e5e7eb",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
            }}
            title="Mostrar/ocultar columnas"
          >
            COLUMNAS
          </button>

          {loadingPedidos && <span style={{ marginLeft: 6 }}>Cargando…</span>}
          {errorPedidos && <span className={styles.error} style={{ marginLeft: 6 }}>{errorPedidos}</span>}
        </div>
      </div>
      {/* ======= /HEADER ======= */}

      {/* Panel de columnas (flotante) */}
      {showColPanel && (
        <div style={{ position: "relative", zIndex: 5 }}>
          <div
            style={{
              position: "absolute", right: 12, top: 6,
              background: "#0b0e14", color: "#e5e7eb",
              border: "1px solid #222633", borderRadius: 10,
              padding: 12, minWidth: 260, boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <strong style={{ fontSize: 13 }}>Columnas visibles</strong>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => { const allOn = {}; for (const c of COLUMNS) allOn[c.key] = true; setColVisibility(allOn); }}
                  style={{ fontSize: 12, background: "transparent", color: "#93c5fd", border: "none", cursor: "pointer" }}
                >
                  Mostrar todo
                </button>
                <button
                  onClick={() => {
                    const allOff = {}; for (const c of COLUMNS) allOff[c.key] = false;
                    allOff["trackindPedido"] = true; setColVisibility(allOff);
                  }}
                  style={{ fontSize: 12, background: "transparent", color: "#fca5a5", border: "none", cursor: "pointer" }}
                >
                  Ocultar todo
                </button>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {COLUMNS.map((c) => (
                <label key={c.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={colVisibility[c.key] !== false}
                    onChange={(e) => setColVisibility((prev) => ({ ...prev, [c.key]: e.target.checked }))}
                  />
                  {c.label}
                </label>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
              <button
                onClick={() => setShowColPanel(false)}
                style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #222633", background: "#111827", color: "#e5e7eb", fontSize: 12, cursor: "pointer" }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* tabla */}
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr>{COLUMNS.map((c) => <HeaderCell key={c.key} col={c} />)}</tr>
          </thead>
          <tbody className={styles.tbody}>
            {filteredPedidos.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className={styles.empty}>
                  {loadingPedidos ? "Cargando datos…" : "Sin resultados."}
                </td>
              </tr>
            ) : (
              filteredPedidos.map((row, idx) => (
                <tr key={`${row.id || "row"}-${idx}`} className={styles.tr}>
                  {COLUMNS.map((c) => {
                    if (colVisibility[c.key] === false) return null;

                    const widthPx = colWidths[c.key];
                    const cellStyle = {
                      width: widthPx ? `${widthPx}px` : undefined,
                      minWidth: widthPx ? `${widthPx}px` : undefined,
                      maxWidth: widthPx ? `${widthPx}px` : undefined,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    };

                    if (c.key === "celularComprador") {
                      const val = row[c.key];
                      return (
                        <td key={c.key} className={styles.td} style={cellStyle}>
                          {val ? <a href={`https://wa.me/${val}`} target="_blank" rel="noreferrer" className={styles.link}>{val}</a> : "—"}
                        </td>
                      );
                    }
                    if (c.key === "ubigeoComprador") {
                      return <td key={c.key} className={styles.td} style={cellStyle}>{cleanUbigeo(row[c.key]) || "—"}</td>;
                    }
                    if (c.key === "total" || c.key === "totalProveedor") {
                      return <td key={c.key} className={styles.td} style={cellStyle}>{formatCLP(row[c.key])}</td>;
                    }
                    if (c.key === "trackindPedido") {
                      const tracking = row[c.key];
                      const norm = normTracking(tracking);
                      return (
                        <td key={c.key} className={styles.td} style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                          <span style={{ display: "inline-flex", alignItems: "center" }}>
                            <TrackingChip value={tracking} />
                            {norm === "Novedad" && (
                              <>
                                <RespuestaEmoji tracking={tracking} respuesta={row.respuestaNovedadTienda} />
                                {/* ⏱ hh:mm desde fechaNovedad → lastUpdatedAt */}
                                <TimeBadge
                                  fromTS={row.fechaNovedad}
                                  toDate={lastUpdatedAt}
                                  title={`Desde ${row.fechaNovedad} hasta última actualización`}
                                />
                              </>
                            )}
                            {norm === "En camino" && (
                              <>
                                {/* ⏱ hh:mm desde fechaRecolectado → lastUpdatedAt */}
                                <TimeBadge
                                  fromTS={row.fechaRecolectado}
                                  toDate={lastUpdatedAt}
                                  title={`Desde ${row.fechaRecolectado} hasta última actualización`}
                                />
                              </>
                            )}
                          </span>
                        </td>
                      );
                    }
                    if (c.key === "contacto") {
                      const current = contactMap[row.id] ?? "no";
                      return (
                        <td key={c.key} className={styles.td} style={cellStyle}>
                          <select
                            value={current}
                            onChange={(e) => setContactMap((m) => ({ ...m, [row.id]: e.target.value }))}
                            style={{ background: "#0b0e14", color: "#e5e7eb", border: "1px solid #222633", borderRadius: 8, padding: "6px 8px", fontSize: 12 }}
                          >
                            <option value="contactado">Contactado</option>
                            <option value="no">No contactado</option>
                          </select>
                        </td>
                      );
                    }

                    return <td key={c.key} className={styles.td} style={cellStyle}>{row[c.key] || "—"}</td>;
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
